import type { ComponentType, LazyExoticComponent } from 'react'
import * as Components from 'routers/routes/lazyComponents'

export enum NAV_KEYS {
  EMISSION = 'EMISSION',
  ISSUE = 'ISSUE',
  DASHBOARDS = 'DASHBOARDS',
  EXPLORE = 'EXPLORE',
  OGMP2 = 'OGMP2',
  RECONCILE = 'RECONCILE',
  CO2E = 'CO2E',
  AUDIT = 'AUDIT',
  LDAR_SIM = 'LDAR_SIM',
  ASSETS = 'ASSETS',
  USER_MANAGEMENT = 'USER_MANAGEMENT',
  FORM_DESIGNER = 'FORM_DESIGNER',
  WORKFLOW_AUTOMATION = 'WORKFLOW_AUTOMATION',
  PROFILES = 'PROFILES',
  SITES = 'SITES',
  DYNAMIC_SCHEDULE = 'DYNAMIC_SCHEDULE',
  PLUGINS = 'PLUGINS',
  UI_PLAYGROUND = 'UI_PLAYGROUND',
}

export interface NavItem {
  key: NAV_KEYS
  label: string
  icon: string
  componentName: string
}

export const NAV_ITEMS = {
  [NAV_KEYS.EMISSION]: {
    key: NAV_KEYS.EMISSION,
    label: 'Emission',
    icon: 'iconEmissions',
    componentName: 'Emissions',
  },
  [NAV_KEYS.ISSUE]: {
    key: NAV_KEYS.ISSUE,
    label: 'Issue',
    icon: 'IssuesIcon',
    componentName: 'NavigationIssueGallery',
  },
  [NAV_KEYS.DASHBOARDS]: {
    key: NAV_KEYS.DASHBOARDS,
    label: 'Dashboard',
    icon: 'MethaneDashboardIcon',
    componentName: 'Dashboard',
  },
  [NAV_KEYS.EXPLORE]: {
    key: NAV_KEYS.EXPLORE,
    label: 'Explore',
    icon: 'MapIcon',
    componentName: 'NavigationMapGallery',
  },
  [NAV_KEYS.OGMP2]: {
    key: NAV_KEYS.OGMP2,
    label: 'OGMP2',
    icon: 'MethaneOGMPIcon',
    componentName: 'OGMP2Page',
  },
  [NAV_KEYS.RECONCILE]: {
    key: NAV_KEYS.RECONCILE,
    label: 'Reconcile',
    icon: 'MethaneReconcileIcon',
    componentName: 'Reconciliation',
  },
  [NAV_KEYS.CO2E]: {
    key: NAV_KEYS.CO2E,
    label: 'CO2e',
    icon: 'MethaneCO2EIcon',
    componentName: 'CO2e',
  },
  [NAV_KEYS.AUDIT]: {
    key: NAV_KEYS.AUDIT,
    label: 'Audit',
    icon: 'MethaneAuditIcon',
    componentName: 'Audit',
  },
  [NAV_KEYS.LDAR_SIM]: {
    key: NAV_KEYS.LDAR_SIM,
    label: 'LDAR-Sim',
    icon: 'LDARSimIcon',
    componentName: 'LDARSim',
  },
  [NAV_KEYS.ASSETS]: {
    key: NAV_KEYS.ASSETS,
    label: 'Assets',
    icon: 'MethaneSitesIcon',
    componentName: 'CAMAssets',
  },
  [NAV_KEYS.USER_MANAGEMENT]: {
    key: NAV_KEYS.USER_MANAGEMENT,
    label: 'User Management',
    icon: 'FaUsersCog',
    componentName: 'UserGallery',
  },
  [NAV_KEYS.FORM_DESIGNER]: {
    key: NAV_KEYS.FORM_DESIGNER,
    label: 'Form Designer',
    icon: 'FormBuilderIcon',
    componentName: 'FormGallery',
  },
  [NAV_KEYS.WORKFLOW_AUTOMATION]: {
    key: NAV_KEYS.WORKFLOW_AUTOMATION,
    label: 'Workflow Automation',
    icon: 'WorkflowIcon',
    componentName: 'WorkflowGallery',
  },
  [NAV_KEYS.PROFILES]: {
    key: NAV_KEYS.PROFILES,
    label: 'Profiles',
    icon: 'ProfileIcon',
    componentName: 'AssetProfileGallery',
  },
  [NAV_KEYS.SITES]: {
    key: NAV_KEYS.SITES,
    label: 'Sites',
    icon: 'BiBuildings',
    componentName: 'NavigationSiteGallery',
  },
  [NAV_KEYS.DYNAMIC_SCHEDULE]: {
    key: NAV_KEYS.DYNAMIC_SCHEDULE,
    label: 'Dynamic Schedule',
    icon: 'FaRoute',
    componentName: 'DynamicScheduling',
  },
  [NAV_KEYS.PLUGINS]: {
    key: NAV_KEYS.PLUGINS,
    label: 'Plugins',
    icon: 'BsPlugin',
  },
  [NAV_KEYS.UI_PLAYGROUND]: {
    key: NAV_KEYS.UI_PLAYGROUND,
    label: 'UI Playground',
    icon: 'AiFillAppstore',
    componentName: 'DevUIPlayground',
  },
} as const

export type NavItemKey = keyof typeof NAV_ITEMS
export type NavItemConfig = typeof NAV_ITEMS[NavItemKey]

interface NavNameAndIcon {
  name: string
  icon: string
}

interface NavLabelAndIcon {
  label: string
  icon: string
}

export const getNavNameAndIcon = (key: NavItemKey): NavNameAndIcon => ({
  name: NAV_ITEMS[key].label,
  icon: NAV_ITEMS[key].icon,
})

export const getNavLabelAndIcon = (key: NavItemKey): NavLabelAndIcon => ({
  label: NAV_ITEMS[key].label,
  icon: NAV_ITEMS[key].icon,
})

export const getComponent = (
  key: NavItemKey
): LazyExoticComponent<ComponentType<unknown>> => {
  const { componentName } = NAV_ITEMS[key]
  return Components[componentName as keyof typeof Components]
}

export const getNavItem = (key: NavItemKey): NavItemConfig => NAV_ITEMS[key]
